import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Nullable } from '../../core';
import { Store } from '../../store/store';
import { StoreNode, BindingProps, Message } from '../../store';
import { Team } from '../../entities/team';
import { InputState, inputGroup, InputGroupState, input } from '../input';
import { notifyError, notifySuccess, notifyLoading } from '../../services/notifications';
import { TeamMemberRoleInput } from '@clipr/lib';
import { CatalogState } from '../layout/catalogState';
import { SyncStatus } from '../../store/syncSchema';
import { RoleItem } from '../createTeamForm/createTeamFormState';
import { TeamMembersWindowMode } from './teamMembersWindowState';
import { TeamMember } from '../../entities/teamMember';
import { TeamInvitation } from '../../entities/teamInvitation';
import { RemoveAccessPopupState } from './removeAccessPopupState';

function checkDisabled(input: InputState | InputGroupState, disable: boolean) {
  return (!!input.loading ||
    input.isSubmitting ||
    disable)
}

const roleSelectorItems = [
  { value: 'Admin', label: 'Admin', description: 'Upload, view, edit, manage library users and videos.' },
  { value: 'Trainer', label: 'Trainer', description: 'Upload, view, edit, and manage library videos.' },
  { value: 'Member', label: 'Member', description: 'Upload and edit library videos.' },
  { value: 'Viewer', label: 'Viewer', description: 'View library videos.' },
  { value: 'Remove Access', label: 'Remove Access', description: '', className: 'red' }
];

export type MemberTargetType = TeamMember | TeamInvitation;

export type MemberItemTargetType = 'member' | 'invitation';

type TeamMembersListProps = BindingProps<{
  syncStatus?: SyncStatus,
  isEndOfList?: boolean,
  fetch?: () => unknown,
  fetchMore?: () => unknown
}>
export class TeamMemberCatalogState
  extends StoreNode {

  readonly memberListInputGroup: InputGroupState;
  readonly memberInputGroup: InputGroupState;

  readonly memberCatalog: CatalogState;

  isLoading = false;
  isSyncing = false;
  isLocked = false;
  mode: TeamMembersWindowMode | null = null;
  teamId: string | null = null;

  constructor(store: Store, props?: TeamMembersListProps) {
    super(store, props);
    makeObservable(this, {
      isLoading: observable,
      isSyncing: observable,
      isLocked: observable,
      mode: observable,
      teamId: observable,
      team: computed,
      isEndOfMemberList: computed,
      addMemberState: action,
      handleUpdateTeamMemberRole: action,
      handleUpdateTeamInvitation: action,
      invoke: action,
      handleDeleteTeamMember: action,
      handleDeleteTeamInvitation: action,
      reset: action,
      lock: action,
      unlock: action,
    });

    this.memberListInputGroup = inputGroup(this, {
      name: 'inviteListInputGroup',
      inputs: () =>
        this.memberList.map((el) => el.lineGroup),
      disabled: (input: InputGroupState) => {
        return (
          input.hasLoadingInputs ||
          input.isSubmitting ||
          this.isLocked
        )
      }
    })
    this.memberInputGroup = inputGroup(this, {
      name: 'inviteInputGroup',
      inputs: [
        this.memberListInputGroup,
      ],
      disabled: input => checkDisabled(input, this.isLocked)
    })

    this.memberCatalog = new CatalogState(this.store, {
      syncStatus: () => this.resolvedProps?.syncStatus,
      isEndOfList: () => this.resolvedProps?.isEndOfList
    });
    this.memberCatalog.listen((msg) => {
      switch (msg.type) {
        case 'scrollToBottom':
        case 'loadMore':
          this.props?.fetchMore();
          break;
      }
    });
  }

  memberList = observable.array();

  get team(): Nullable<Team> {
    return this.store.teamManager.getTeam(this.teamId);
  }

  get isEndOfMemberList(): boolean {
    return this.resolvedProps?.isEndOfList
  }

  addMemberState = (member: MemberTargetType, type?: MemberItemTargetType) => {
    const memberState: TeamMemberItemState = new TeamMemberItemState(this.store, {
      type: type,
      handleUpdate: type === 'member' ?
        (id, role) =>
          this.handleUpdateTeamMemberRole(id, role) :
        (id, role) =>
          this.handleUpdateTeamInvitation(id, role),
      handleDelete: type === 'member' ?
        id =>
          this.handleDeleteTeamMember(id) :
        id =>
          this.handleDeleteTeamInvitation(id)
    });
    memberState.teamId = this.teamId;
    memberState.target = member || null;
    memberState.mode = this.mode || null;
    memberState.initValues();
    this.memberList.push(memberState);
    return memberState;
  }

  async handleUpdateTeamMemberRole(id: string, role: TeamMemberRoleInput) {
    this.isLoading = true;
    const member = this.team?.getMember(id);
    notifyLoading(this, "Updating Member Role...");
    const name = member?.name || null;
    const [, err] = await this.team?.apiUpdateTeamMemberRole({
      teamId: this.team?.id,
      userId: member?.userId!,
      role: role
    }) || [null];

    runInAction(async () => {
      if (err) {
        this.isLoading = false;
        notifyError(this, `Library Member ${name} update failed.`);
        return;
      }
      this.isLoading = false;
      notifySuccess(this, `Library Member ${name} updated successfully.`);
      await Promise.all([
        this.props?.fetch(),
        this.team?.apiFetchInvitations()
      ])
    })
  }

  async handleUpdateTeamInvitation(id: string, role: TeamMemberRoleInput) {
    this.isLoading = true;
    const invitation = this.team?.getInvitation(id);
    notifyLoading(this, "Updating Invitation...");
    const name = invitation?.name;
    const [, err] = await this.team?.apiUpdateTeamInvitation({
      teamId: this.team?.id,
      email: name!,
      role: role
    }) || [null];
    if (err) {
      this.isLoading = false;
      notifyError(this, `Invitation for ${name} update failed.`);
      return;
    }
    await Promise.all([
      this.props?.fetch(),
      this.team?.apiFetchInvitations()
    ])
    notifySuccess(this, `Invitation for ${name} updated successfully.`);
    this.isLoading = false;
  }

  invoke = (type: string, payload?: { id?: string; email?: string }) => {

    switch (type) {
      case 'openRemoveAccess':
        if (!this.team) return;
        const removeAccessPopup = this.store.removeAccessPopup;
        const removeAccessPopupListener = (msg: Message<RemoveAccessPopupState>) => {
          switch (msg.type) {
            case 'open':
              break;

            case 'close':
              removeAccessPopup.unlisten(removeAccessPopupListener);
              break;

            case 'removedAccess':
              this.fetchCatalogEntities();
              removeAccessPopup.unlisten(removeAccessPopupListener);
              break;
          }
        }
        removeAccessPopup.listen(removeAccessPopupListener);
        if (payload?.id)
          removeAccessPopup.open({
            teamId: this.team?.id,
            userId: payload?.id
          });
        else if (payload?.email)
          removeAccessPopup.open({
            teamId: this.team?.id,
            email: payload?.email
          });
        break;
    }
  }

  async fetchCatalogEntities() {
    this.isSyncing = true;
    await Promise.all([
      this.props?.fetch(),
      this.team?.apiFetchInvitations()
    ]);
    this.isSyncing = false;
  }

  async handleDeleteTeamMember(id: string) {
    if (!this.team?.id)
      return;

    this.invoke('openRemoveAccess', { id });
  }

  async handleDeleteTeamInvitation(id: string) {
    const invitation = this.team?.getInvitation(id);
    if (!this.team?.id || !invitation)
      return;

    this.invoke('openRemoveAccess', { email: invitation.email });
  }

  reset() {
    this.memberList.clear();
  }

  lock() {
    this.isLocked = true;
    this.memberList.forEach(item => item.lock());
  }

  unlock() {
    this.isLocked = false;
    this.memberList.forEach(item => item.unlock());
  }

}

type TeamMemberItemProps = {
  type?: MemberItemTargetType,
  handleDelete?: (id: string) => void,
  handleUpdate?: (id: string, role: TeamMemberRoleInput) => void
};

export class TeamMemberItemState
  extends StoreNode {

  readonly lineGroup: InputGroupState;
  readonly type: MemberItemTargetType;
  // readonly memberName: InputState;
  readonly roleInput: InputState;

  isSyncing = false;
  isSynced = false;
  isError = false;
  isLocked = false;

  target: Nullable<MemberTargetType> = null;
  teamId: Nullable<string> = null;
  mode: Nullable<TeamMembersWindowMode> = null;

  constructor(store: Store, props?: TeamMemberItemProps) {
    super(store, props);
    makeObservable(this, {
      isSyncing: observable,
      isSynced: observable,
      isError: observable,
      isLocked: observable,
      target: observable,
      teamId: observable,
      mode: observable,
      roleItems: computed,
      team: computed,
      targetName: computed,
      isInvitation: computed,
      handleRoleChange: action,
      initValues: action,
      setSyncing: action,
      setSynced: action,
      setError: action,
      lock: action,
      unlock: action,
    });

    this.roleInput = input(this, {
      name: 'role',
      showStatusMessage: false,
      disabled: input => {
        return checkDisabled(input, this.isLocked) || this.target?.role.toLowerCase() === 'owner';
      },
      placeholder: 'Role',
      selectorItems: () => this.roleItems,
      onChange: async input => this.handleRoleChange(input)
    });
    this.lineGroup = inputGroup(this, {
      name: 'inviteLine',
      inputs: [
        // this.memberName,
        this.roleInput
      ],
      disabled: (input: InputGroupState) => {
        return (
          input.hasLoadingInputs ||
          input.isSubmitting ||
          this.isSynced ||
          this.isSyncing ||
          this.isLocked
        )
      }
    });

    this.type = props?.type || 'member';
  }

  get roleItems(): RoleItem[] {
    return this.store?.user?.hasPermission('AddTeamTrainer') ?
      roleSelectorItems :
      roleSelectorItems.filter(role => role.value !== 'Trainer');
  }

  get team(): Nullable<Team> {
    return this.store.teamManager.getTeam(this.teamId);
  }

  get targetName() {
    return this.target?.name || null
  }

  get isInvitation() {
    return this.type === 'invitation';
  }

  async handleRoleChange(input: InputState) {
    const value = input?.value || null;

    switch (value) {
      case 'Remove Access':
        await this.props?.handleDelete(this.target?.id);
        break;
      default:
        await this.props?.handleUpdate(this.target?.id, value);
        break;
    }
  }

  initValues() {
    this.roleInput.value = this.target?.role || null;
    // this.memberName.value = this.targetName || null;
  }

  setSyncing(value: boolean) {
    this.isSyncing = value;
  }

  setSynced(value: boolean) {
    this.isSynced = value;
  }

  setError(value: boolean) {
    this.isError = value;
  }

  lock() {
    this.isLocked = true;
  }

  unlock() {
    this.isLocked = false;
  }
}